import { LiabilityInsuranceSortField, PaginatedLiabilityInsuranceQueryVariables } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedLiabilityInsuranceQueryVariables) => void;
}

/**
 * LiabilityInsuranceSortingDropdown component
 * Provides a dropdown menu for sorting liability insurance data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const LiabilityInsuranceSortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {LiabilityInsuranceSortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: LiabilityInsuranceSortField): string => {
    switch (field) {
    case LiabilityInsuranceSortField.ActiveFrom:
      return t("components:filters.liabilityInsurance.sortfields.activeFrom");
    case LiabilityInsuranceSortField.LiabilityInsurance:
      return t("components:filters.liabilityInsurance.sortfields.liabilityInsurance");
    case LiabilityInsuranceSortField.ProfessionalAssociation:
      return t("components:filters.liabilityInsurance.sortfields.professionalAssociation");
    default:
      return field;
    }
  };

  const liabilityInsuranceSortConfig = {
    paginationStateKey: "paginatedLiabilityInsurance" as keyof PaginationState,
    sortFields: LiabilityInsuranceSortField,
    getSortFieldName,
    defaultSortField: LiabilityInsuranceSortField.ActiveFrom,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: LiabilityInsuranceSortField; order: any } | null): PaginatedLiabilityInsuranceQueryVariables => ({
      first: displayAmount,
      filters: {
        activeFrom: filters.activeFrom,
        liabilityInsurance: filters.liabilityInsurance,
        professionalAssociation: filters.professionalAssociation,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      activeFrom: paginationState.filters?.activeFrom,
      liabilityInsurance: paginationState.filters?.liabilityInsurance,
      professionalAssociation: paginationState.filters?.professionalAssociation,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={liabilityInsuranceSortConfig}
    />
  );
};

export default LiabilityInsuranceSortingDropdown;
